import { PropsWithChildren } from "react";
import { Box, IconButton } from "@mui/material";
import { Sell } from "@mui/icons-material";
import Typography from "../Typography";
import { getCurrentSizeProfile, responsiveFontSize } from "../Typography/style";
import { Link } from "react-router-dom";
import { objectToSearchParams } from "@common/utils/filters/mappers/common";
import { TicketsFilters } from "@services/tickets/types";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { canViewTicket } from "@common/utils/user";

type TicketBadgeCommonProps = {
  notificationsNumber?: number;
  previousPage?: string;
  replaceHistoryRecord?: boolean;
};

type TicketBadgeAccountProps = {
  accountId?: string;
  locationId?: never;
  roomId?: never;
  deviceId?: never;
  meetingId?: never;
};
type TicketBadgeLocationProps = {
  accountId?: never;
  locationId?: string;
  roomId?: never;
  deviceId?: never;
  meetingId?: never;
};
type TicketBadgeRoomProps = {
  accountId?: never;
  locationId?: never;
  roomId?: string;
  deviceId?: never;
  meetingId?: never;
};
type TicketBadgeDeviceProps = {
  accountId?: never;
  locationId?: never;
  roomId?: never;
  deviceId?: string;
  meetingId?: never;
};
type TicketBadgeMeetingProps = {
  accountId?: never;
  locationId?: never;
  roomId?: never;
  deviceId?: never;
  meetingId?: string;
};

type TicketBadgeUnionProps =
  | TicketBadgeAccountProps
  | TicketBadgeLocationProps
  | TicketBadgeRoomProps
  | TicketBadgeDeviceProps
  | TicketBadgeMeetingProps;

type TicketBadgeProps = TicketBadgeCommonProps & TicketBadgeUnionProps;

const isAccountBadge = (
  x: TicketBadgeUnionProps
): x is TicketBadgeAccountProps =>
  (x as TicketBadgeAccountProps).accountId !== undefined;
const isLocationBadge = (
  x: TicketBadgeUnionProps
): x is TicketBadgeLocationProps =>
  (x as TicketBadgeLocationProps).locationId !== undefined;
const isRoomBadge = (x: TicketBadgeUnionProps): x is TicketBadgeRoomProps =>
  (x as TicketBadgeRoomProps).roomId !== undefined;
const isDeviceBadge = (x: TicketBadgeUnionProps): x is TicketBadgeDeviceProps =>
  (x as TicketBadgeDeviceProps).deviceId !== undefined;
const isMeetingBadge = (
  x: TicketBadgeUnionProps
): x is TicketBadgeMeetingProps =>
  (x as TicketBadgeMeetingProps).meetingId !== undefined;
const getLink = (props: TicketBadgeUnionProps) => {
  let link = "";
  const filter: TicketsFilters = { status: ["Open"] };
  if (isAccountBadge(props)) link = `/assets/accounts/${props.accountId}`;
  if (isLocationBadge(props)) link = `/assets/locations/${props.locationId}`;
  if (isRoomBadge(props)) link = `/assets/rooms/${props.roomId}`;
  if (isDeviceBadge(props)) link = `/assets/devices/${props.deviceId}`;
  if (isMeetingBadge(props)) link = `/meetings/${props.meetingId}`;

  return `${link}?tab=Tickets&${objectToSearchParams(filter)}`;
};
const TicketBadge = (props: TicketBadgeProps) => {
  const {
    notificationsNumber = 0,
    previousPage,
    replaceHistoryRecord = false,
  } = props;
  const [user] = useAuthenticatedUser();

  const ConditionalLink = ({
    children,
  }: PropsWithChildren<TicketBadgeProps>) => {
    return canViewTicket(user) ? (
      <Link
        to={getLink(props)}
        replace={replaceHistoryRecord}
        state={{ previous: previousPage }}
      >
        {children}
      </Link>
    ) : (
      <>{children}</>
    );
  };
  return (
    <ConditionalLink>
      <IconButton
        sx={{
          position: "relative",
        }}
        data-testid="tickets-button"
      >
        <Sell
          fontSize="large"
          sx={(theme) => ({
            color: theme.palette.common.grey.light,
          })}
        />
        <Box
          sx={(theme) => ({
            position: "absolute",
            top: 0,
            right: 0,
            color: "black",
            borderRadius: "50%",
            fontSize: getCurrentSizeProfile().smaller,
            ...responsiveFontSize(theme, "smaller"),
            width: "2em",
            height: "2em",
            textAlign: "center",
            backgroundColor: "white",
            border: "2px solid white",
            "& .MuiTypography-root": {
              lineHeight: "0.9em",
            },
          })}
        >
          <Typography size="small" realTime>
            {notificationsNumber > 9 ? "9+" : notificationsNumber}
          </Typography>
        </Box>
      </IconButton>
    </ConditionalLink>
  );
};

export default TicketBadge;
